import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import axios from 'axios';
import config from '../../config.js';

import '../../styles/global.css';

import PageHeader from '../../components/general/pageHeader.js';
import PageTable from '../../components/general/pageTable.js';
import Filter from '../../components/general/filters.js';
import CreateParcelForm from '../../components/forms/createParcelForm.js';
import EditParcelForm from '../../components/forms/editParcelForm.js';
import DeleteConfirmation from "../../components/general/deleteConfirmation.js";
import Loading from '../../components/general/loading.js';
import useAuthenticate from '../../components/authentication/authenticate.js';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { Tooltip } from '@mui/material';

const Parcels = () => {
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedParcel, setSelectedParcel] = useState(null);
  const [addOverlayOpen, setAddOverlayOpen] = useState(false);
  const [editOverlayOpen, setEditOverlayOpen] = useState(false);
  const [deleteOverlayOpen, setDeleteOverlayOpen] = useState(false);

  // Filters
  const [carrierFilter, setCarrierFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [notifiedFilter, setNotifiedFilter] = useState('');
  const [filterPanelVisible, setFilterPanelVisible] = useState(false);

  // Filtered parcels
  const [filteredParcels, setFilteredParcels] = useState([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const { isLoading } = useAuthenticate();

  const queryClient = useQueryClient();

  const { data: properties } = useQuery({
    queryKey: ['v2-properties'],
    queryFn: fetchProperties,
  });

  const { data: units = [], isFetching: isUnitsLoading } = useQuery({
    queryKey: ['v2-units', selectedProperty?.value],
    queryFn: () => fetchUnitsByProperty(selectedProperty?.value),
    enabled: !!selectedProperty,
  });

  const { data: parcels = { results: [], totalPages: 0 }, isLoading: isTableLoading } = useQuery({
    queryKey: ['v2-parcels', selectedUnit?.value, currentPage],
    queryFn: () => fetchParcelsByUnit(selectedUnit?.value, currentPage, rowsPerPage),
    enabled: !!selectedUnit,
    keepPreviousData: true,
  });

  useEffect(() => {
    if (properties?.length > 0) {
      setSelectedProperty((prev) => {
        if (!prev || prev.value !== properties[0].value) {
          return properties[0];
        }
        return prev;
      });
    }
  }, [properties]);

  useEffect(() => {
    if (units?.length > 0) {
      setSelectedUnit((prev) => {
        if (!prev || prev.value !== units[0].value) {
          return units[0];
        }
        return prev;
      });
    } else {
      setSelectedUnit(null);
    }
  }, [units]);

  useEffect(() => {
    if (!selectedUnit || !parcels?.results || parcels.results.length === 0) {
      setFilteredParcels([]);
      setSelectedParcel(null);
      return;
    }

    let filtered = [...parcels.results];

    if (carrierFilter) {
      filtered = filtered.filter((p) =>
        p.carrier?.toLowerCase().includes(carrierFilter.toLowerCase())
      );
    }

    if (statusFilter) {
      filtered = filtered.filter((p) =>
        p.status?.toLowerCase().includes(statusFilter.toLowerCase())
      );
    }

    if (notifiedFilter) {
      const boolFilter = notifiedFilter === 'Yes';
      filtered = filtered.filter((p) => Boolean(p.notified) === boolFilter);
    }

    setFilteredParcels(filtered);
  }, [selectedUnit, carrierFilter, statusFilter, notifiedFilter, parcels]);

  // Reset pagination on filter or unit change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedUnit, carrierFilter, statusFilter, notifiedFilter]);

  // CRUD Handlers
  const handleUpdate = (parcel) => {
    setSelectedParcel(parcel._id);
    setEditOverlayOpen(true);
  };

  const handleDelete = (parcel) => {
    setSelectedParcel(parcel._id);
    setDeleteOverlayOpen(true);
  };

  const confirmCreate = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['v2-parcels', selectedUnit?.value],
    });
  };

  const confirmUpdate = async () => {
    await queryClient.invalidateQueries(['v2-parcels', selectedUnit?.value]);
  };

  const confirmDelete = async () => {
    if (!selectedParcel) return;

    try {
      await axios.delete(`${config.API_URL}/v2/parcel/${selectedParcel}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });

      setDeleteOverlayOpen(false);
      setSelectedParcel(null);
      toast.success('Parcel deleted successfully!');
      await queryClient.invalidateQueries(['v2-parcels', selectedUnit?.value]);
    } catch (err) {
      console.error('Failed to delete parcel:', err);
      toast.error('Failed to delete parcel.');
    }
  };

  const totalPages = parcels?.totalPages || 1;
  const maxVisibleButtons = 10;
  const startPage = Math.floor((currentPage - 1) / maxVisibleButtons) * maxVisibleButtons + 1;
  const endPage = Math.min(startPage + maxVisibleButtons - 1, totalPages);

  return (
    <div className="w-full full-page">
      <PageHeader pageName="Parcels" />
      {isLoading && !selectedProperty ? (
        <Loading />
      ) : (
        <>
          <div className="page-header" style={{ alignItems: 'flex-end' }}>
            <div className="account-dropdown">
              <Autocomplete
                options={properties}
                value={selectedProperty}
                onChange={(e, newVal) => setSelectedProperty(newVal || null)}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                getOptionLabel={(option) =>
                  typeof option === 'string'
                    ? option
                    : option?.label || option?.name || ''
                }
                renderInput={(params) => (
                  <TextField {...params} label="Select Property" variant="outlined" fullWidth />
                )}
                sx={{ width: '300px', marginBottom: '15px' }}
              />
              <Autocomplete
                options={units}
                value={selectedUnit}
                onChange={(e, val) => setSelectedUnit(val)}
                loading={isUnitsLoading}
                getOptionLabel={(option) => option.label || ''}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Unit"
                    variant="outlined"
                    fullWidth
                    helperText={isUnitsLoading ? 'Loading units...' : ''}
                  />
                )}
                sx={{ width: '300px' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', height: '50px' }}>
              <button
                className={`filter-button ${filterPanelVisible ? 'active' : ''}`}
                onClick={() => setFilterPanelVisible((prev) => !prev)}
              >
                <i className="fas fa-filter"></i> Filter
              </button>

              <Tooltip
                title={!selectedUnit ? "Select a unit to create" : ""}
                arrow
                disableInteractive
              >
                <span style={{ display: 'inline-block', height: '100%' }}>
                  <button
                    className="header-button"
                    onClick={() => setAddOverlayOpen(true)}
                    style={{ height: '100%' }}
                    disabled={!selectedUnit}
                  >
                    <i className="fas fa-add"></i>Parcel
                  </button>
                </span>
              </Tooltip>
            </div>

          </div>
          {filterPanelVisible && (
            <div className="filter-container">
              <Filter
                placeholder="Carrier"
                options={[...new Set(parcels?.results?.map((p) => p.carrier))]}
                value={carrierFilter}
                onChange={setCarrierFilter}
              />
              <Filter
                placeholder="Status"
                options={[...new Set(parcels?.results?.map((p) => p.status))]}
                value={statusFilter}
                onChange={setStatusFilter}
              />
              <Filter
                placeholder="Notified"
                options={['Yes', 'No']}
                value={notifiedFilter}
                onChange={setNotifiedFilter}
              />
            </div>
          )}

          <div className="page-content">
            {!selectedUnit ? (
            <div className="info-text">
              No unit selected
            </div>
            ) : isTableLoading ? (
              <Loading />
            ) : (
              <div className="table-wrapper">
                <PageTable
                  rows={
                    carrierFilter || statusFilter || notifiedFilter
                      ? filteredParcels
                      : parcels.results
                  }
                  columns={[
                    { label: 'Carrier', accessor: 'carrier', width: '20%' },
                    { label: 'Tracking Number', accessor: 'trackingNumber', width: '20%' },
                    { label: 'Description', accessor: 'description', width: '20%' },
                    {
                      label: 'Status',
                      accessor: (row) => {
                        const statusColors = {
                          Pending: 'bg-blue-900',
                          Delivered: 'bg-green-500',
                          'Picked Up': 'bg-yellow-500',
                          Lost: 'bg-red-600',
                          Returned: 'bg-gray-500',
                        };
                        const colorClass = statusColors[row.status] || 'bg-gray-400';

                        return (
                          <span className={`text-white text-sm w-[100px] inline-block text-center py-1 rounded-full ${colorClass}`}>
                            {row.status}
                          </span>
                        );
                      },
                      width: '15%',
                    },
                    { label: 'Notified', accessor: (row) => (row.notified ? 'Yes' : 'No'), width: '10%' },
                    {
                      label: 'Delivery Date',
                      accessor: (row) => new Date(row.deliveryDate).toLocaleDateString(),
                      width: '15%',
                    },
                  ]}
                  hasButtons={true}
                  hasDelete={true}
                  hasEdit={true}
                  onDelete={handleDelete}
                  onEdit={handleUpdate}
                />

                {parcels.totalPages > 1 && (
                  <div className="pagination-controls">
                    <div className="pagination-buttons">
                      <button
                        className="pagination-button"
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                      >
                        <i className="fas fa-chevron-left"></i>
                      </button>

                      {startPage > 1 && (
                        <button className="pagination-page" onClick={() => setCurrentPage(startPage - 1)}>
                          ...
                        </button>
                      )}

                      {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                        const pageNum = startPage + i;
                        return (
                          <button
                            key={pageNum}
                            className={`pagination-page ${currentPage === pageNum ? 'active' : ''}`}
                            onClick={() => setCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </button>
                        );
                      })}

                      {endPage < totalPages && (
                        <button className="pagination-page" onClick={() => setCurrentPage(endPage + 1)}>
                          ...
                        </button>
                      )}

                      <button
                        className="pagination-button"
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                      >
                        <i className="fas fa-chevron-right"></i>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
      {addOverlayOpen && selectedUnit && (
        <CreateParcelForm
          unitId={selectedUnit.value}
          onParcelCreated={confirmCreate}
          onClose={() => setAddOverlayOpen(false)}
        />
      )}
      {editOverlayOpen && selectedParcel && (
        <EditParcelForm
          parcelId={selectedParcel}
          onClose={() => setEditOverlayOpen(false)}
          onParcelUpdated={confirmUpdate}
        />
      )}
      {deleteOverlayOpen && selectedParcel && (
        <DeleteConfirmation
          setIsOverlayOpen={setDeleteOverlayOpen}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
};

const fetchProperties = async () => {
  const response = await axios.get(`${config.API_URL}/v2/property`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
  });
  return response.data.map((item) => ({
    value: item._id,
    label: item.name,
  }));
};

const fetchUnitsByProperty = async (propertyId) => {
  const response = await axios.get(`${config.API_URL}/v2/unit/property/${propertyId}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
  });
  return response.data.map(item => ({
    value: item._id,
    label: item.unitNumber,
  }));
};

const fetchParcelsByUnit = async (unitId, page = 1, limit = 10) => {
  if (!unitId) return { results: [], page: 1, totalPages: 0, totalResults: 0 };
  const response = await axios.get(`${config.API_URL}/v2/parcel`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
    params: { unit: unitId, page, limit, sortBy: 'deliveryDate', sortOrder: 'desc' },
  });
  return response.data;
};

export default Parcels;
